<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent" v-if="component"></column-header>
    <div class="tabulator-parent">
      <div class="row">
				<div class="col-md-12">
          <h3 v-if="customer.name">{{ customer.name }}</h3>
          <h3 v-else>{{ _('New Customer') }}</h3>
        </div>
      </div>
      <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true)" />
      <editor-tabs :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <transition-group name="fade">
          <div v-bind="tabPanel('general')" class="tab-pane active" id="general" v-if="isTabActive('general')" key="general">
            <div class="row">
              <div class="col-xs-12">
                <h3>{{ _('General customer information') }}</h3>
              </div>
            </div>
            <div class="row" v-if="customer.id">
              <div class="col-xs-12">
                <p>{{ _('Any changes made to these fields will be stored locally in your shop.') }}
                {{ _('If the customer updates their information via My Johku or through the order process, your changes will be overwritten.') }}</p>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="name">{{ _('Name') }}</label>
                  <input type="text" class="form-control" id="customerName" :placeholder="_('Enter customer name')" name="name" v-model="customer.name" />
                </div>
                <div class="form-group">
                  <label for="address">{{ _('Address') }}</label>
                  <input type="text" class="form-control" id="customerAddress" :placeholder="_('Enter street address')" name="address" v-model="customer.address" />
                </div>
                <div class="form-group">
                  <label for="zip">{{ _('Postal code') }}</label>
                  <input type="text" class="form-control" id="customerZip" :placeholder="_('Enter postal code')" name="zip" v-model="customer.zip" />
                </div>
                <div class="form-group">
                  <label for="city">{{ _('City') }}</label>
                  <input type="text" class="form-control" id="customerCity" :placeholder="_('Enter city')" name="city" v-model="customer.city" />
                </div>
                <div class="form-group">
                  <label for="country">{{ _('Country') }}</label>
                  <select class="form-control" name="country" id="customerCountry" v-model="customer.country">
                    <option value="FI">{{ countries['FI'] }}</option>
                    <template v-for="(country, id) in countries" :key="id">
                      <option v-if="isPriorityCountry(id) && id !== 'FI'" :value="id">{{ country }}</option>
                    </template>
                    <option class="select-separator" disabled="disabled">────────────</option>
                    <option v-for="(country, id) in countries" :value="id">{{ country }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <select class="form-control" v-model="allowMarketing">
                    <option value="true">{{ _('Marketing allowed') }}</option>
                    <option value="false">{{ _('Marketing NOT allowed') }}</option>
                  </select>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group" v-if="isNetvisorEnabled">
                  <label for="netvisor">{{ _('Netvisor customer') }}</label>
                  <div class="input-group">
                    <model-list-select :list="netvisorCustomers" option-value="id" option-text="name" v-model="netvisorCustomer" @searchchange="searchNetvisorCustomers" :custom-text="formatNetvisorCustomer" :placeholder="_('Type to search customers')">
                    </model-list-select>
                    <span class="input-group-btn">
                      <button class="btn btn-default" type="button" v-on:click="updateNetvisorCustomers"><i class="fa fa-fw fa-refresh"></i></button>
                    </span>
                  </div>
                </div>
                <div class="form-group">
                  <label for="phone">{{ _('Phone') }}</label>
                  <phone-number-input v-model="customerPhone" :placeholder="_('Enter customer phone')" />
                </div>
                <div class="form-group">
                  <label for="email">{{ _('Email') }}</label>
                  <input type="email" class="form-control" id="customerEmail" :placeholder="_('Enter email address')" name="email" v-model="customer.email" />
                </div>
                <div class="form-group" v-if="!customer.id">
                  <p><i>{{ _('By clicking Save I certify that I have authorization from the person and/or company in question to add their data to Johku customer register.') }}</i></p>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('identification')" class="tab-pane active" id="identification" v-if="isTabActive('identification')" key="identification">
            <div class="row" v-if="customer.id">
              <div class="col-md-6">
                <h3>{{ _('Identification') }}</h3>
                <template v-if="customer.identifier">
                  <label>{{ _('Johku customer QR code') }}</label>
                  <div class="form-group">
                    <img :src="identCodeUrl" />
                  </div>
                </template>
              </div>
              <div class="col-md-6">
                <h3>{{ _('Loyalty cards') }}</h3>
                <draggable-multi-selector v-model="customer.sharedLoyaltycard" :items="customer.sharedLoyaltycard" itemProperty="name" :selectorTitle="_('Link a Loyalty card')" />
                <button class="btn btn-success" type="button" v-on:click="addLoyaltyCard">{{ _('New Loyalty Card') }}</button>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('links')" class="tab-pane active" id="links" v-if="isTabActive('links')" key="links">
            <div class="row">
              <div class="col-md-6" v-if="customer.id">
                <h3>{{ _('Billing Customers') }}</h3>
                <draggable-multi-selector v-model="customer.sharedBillingcustomer" :items="allBillingCustomers" itemProperty="company" :selectorTitle="_('Link a Billing Customer')" />
                <button class="btn btn-success" type="button" v-on:click="addBillingCustomer">{{ _('New Billing Customer') }}</button>
                <h3>{{ _('Default Billing Customer') }}</h3>
                <select class="form-control" v-model="customer.billingcustomer_id">
                  <option value="">{{ _('Not selected') }}</option>
                  <option v-for="bc in customer.sharedBillingcustomer" :value="bc.id">{{ bc.company }}</option>
                </select>
              </div>
              <div class="col-md-6" v-if="customer.id">
                <h3>{{ _('Cost objects') }}</h3>
                <draggable-multi-selector v-model="customer.sharedDimension" :items="allDimensions" :groups="true" :single-select="isBrioxAccountingMode" itemProperty="name" :selectorTitle="_('Link a Cost object')" />
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('orders')" class="tab-pane active" id="orders" v-if="isTabActive('orders')" key="orders">
            <datatable :url="ordersUrl" :options="tableOptions" ref="datatable" v-if="customer.id && isTabActive('orders')" />
          </div>
          <div v-bind="tabPanel('subscriptions')" class="tab-pane active" id="subscriptions" v-if="isTabActive('subscriptions')" key="subscriptions">
            <subscriptions-panel v-if="customer.id" :customer-id="customer.id" :show-charges="true" :details-trail="componentTrail" />
          </div>
        </transition-group>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <div class="flex-row">
          <button class="btn btn-danger" v-on:click="remove(_('Are you sure you want to delete the customer? Deletion can\'t be undone.'))" type="button"><i class="fa fa-fw fa-trash"></i><span class="hidden-xs"> {{ _('Delete customer') }}</span></button>
        </div>
        <div class="flex-row">
          <button class="btn btn-default" type="button" v-if="customer.id && isFeatureEnabled('customerreports')" v-on:click="openPurchaseReport"><i class="fa fa-fw fa-book"></i><span class="hidden-xs"> {{ _('Customer purchases report') }}</span></button>
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i><span class="hidden-xs"> {{ _('Close') }}</span></button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>

import axios from 'axios'
import changewatcher from '~/lib/changewatcher'
import components from '~/lib/components'
import formatters from '~/lib/formatters'
import modals from '~/lib/modals'
import settingshelpers from '~/lib/settingshelpers'
import debounce from 'lodash/debounce'
import { mapGetters } from 'vuex'
import { ModalBillingCustomerDetails as BillingCustomerDetails, SubscriptionsPanel } from '#components'
import Datatable from '~/components/Datatable'
import DraggableMultiSelector from '~/components/DraggableMultiSelector'
import { ModelListSelect } from 'vue-search-select'
import PhoneNumberInput from '~/components/PhoneNumberInput'

export default {
  data () {
    const $this = this
    const _ = useNuxtApp().$translate
    return {
      showTable: false,
      customer: {},
      netvisorCustomer: {},
      netvisorCustomers: [],
      allBillingCustomers: [],
      allDimensions: [],
      tableOptions: {
        rowFormatter: $this.orderRowFormatter(),
        responsiveLayoutCollapseFormatter: $this.collapseFormatter(),
        columns: [{
          title: _('Order no.'),
          field: 'id',
          width: 80,
          responsive: 0
        },
        {
          title: _('Customer name'),
          field: 'name',
          widthGrow: 4,
          formatter: $this.orderCustomerFormatter(),
          responsive: 0
        },
        {
          title: _('Total'),
          field: 'totalWithDelivery',
          widthGrow: 2,
          formatter: $this.numberFormatter(),
          hozAlign: 'right'
        },
        {
          title: _('Status'),
          field: 'paid',
          widthGrow: 2,
          formatter: $this.paidFormatter()
        },
        {
          title: _('Order date'),
          field: 'orderdate',
          widthGrow: 3,
          formatter: $this.dateAndTimeFormatter()
        },
        {
          title: _('Processed'),
          field: 'processed',
          widthGrow: 2,
          formatter: $this.processedFormatter()
        },
        {
          title: _('Delivery method'),
          field: 'delivery',
          widthGrow: 2,
          formatter: $this.deliveryMethodFormatter()
        }
        ],
        initialSort: [{
          column: 'id',
          dir: 'desc'
        }],
        rowClick (e, row) {
          const id = row.getCell('id').getValue()
          const name = row.getCell('name').getValue()
          $this.displayOrder(id, name)
        }
      }
    }
  },
  props: ['selectedCustomer'],
  mixins: [changewatcher, components, formatters, modals, settingshelpers],
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'general', label: this._('General') },
        { id: 'identification', label: this._('Identification') },
        { id: 'links', label: this._('Links') },
        { id: 'orders', label: this._('Orders') },
        { id: 'subscriptions', label: this._('Subscriptions'), hidden: !this.isFeatureEnabled('paymentplans') }
      ])
    },
    ...mapGetters(['countries']),
    isNewCustomer () {
      return !this.customer || !this.customer.id
    },
    allowMarketing: {
      get () {
        return this.customer && !!parseInt(this.customer.allowmarketing)
      },
      set (newValue) {
        if (newValue === 'true' || parseInt(newValue)) {
          this.customer.allowmarketing = 1
        } else {
          this.customer.allowmarketing = 0
        }
      }
    },
    customerPhone: {
      get () {
        if (!this.customer || !this.customer.phone) {
          return ''
        }
        return this.customer.phone
      },
      set (newValue) {
        if (newValue) {
          newValue = newValue.replace(/\s/g, '')
        }
        this.customer.phone = newValue
      }
    },
    customerTitle () {
      if (this.isNewCustomer) {
        return this._('Create a new customer')
      }
      return this.customer.name
    },
    isNetvisorEnabled () {
      return parseInt(this.settings.pay_option13)
    },
    ordersUrl () {
      return this.baseDomain + this.shopId + '/customers/' + this.customer.id + '/orders.json?apikey=' + this.apiKey + '&rawformat=true'
    },
    identCodeUrl () {
      return 'https://qrcode.johku.com/?code=' + this.baseDomain + this.shopId + '/qr/U' + this.customer.identifier
    }
  },
  methods: {
    isPriorityCountry (id) {
      return ['SE', 'NO', 'DK', 'EE', 'RU'].indexOf(id) !== -1
    },
    isConditionalTab(tab) {
      if (tab === 'identification' || tab === 'links' || tab === 'orders' || tab === 'subscriptions') {
        return this.isNewCustomer
      }
      return false
    },
    openPurchaseReport () {
      if (!this.customer || !this.customer.id) {
        return
      }
      const params = {
        type: 'customerpurchases',
        id: 0,
        format: 'xlsx',
        output: 'download',
        start: '1 January 1970 00:00',
        end: 'today',
        emails: this.customer.email || ''
      }
      this.EventBus.$emit('showComponent', {
        shopId: this.shopId,
        component: 'CreateReport',
        title: this._('Create report'),
        initData: {
          setCreateReportParams: params
        },
        events: [{
          setCreateReportParams: params
        }]
      })
    },
    loadCustomer (id) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/customers/' + id + '.json?apikey=' + this.apiKey)
        } catch (e) {
          this.loading = false
          this.showError(e)
          reject()
          return
        }
        if (response.data.netvisorid) {
          try {
            this.netvisorCustomer = await this.loadNetvisorCustomer(response.data.netvisorid)
            this.netvisorCustomers = [this.netvisorCustomer]
          } catch(e) {}
        }
        this.customer = response.data
        this.storeState()
        this.loading = false
        resolve(this.customer)
      })
    },
    loadNetvisorCustomer (id) {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/netvisorcustomers/' + id + '.json?apikey=' + this.apiKey)
        } catch(e) {
          reject()
          return
        }
        resolve(response.data)
      })
    },
    getSaveData () {
      return { ...this.customer }
    },
    async doRemove () {
      let response
      try {
        this.deleted = true
        response = await axios.delete(this.baseDomain + this.shopId + '/customers/' + this.customer.id + '.json?apikey=' + this.apiKey)
      } catch(e) {
        this.deleted = false
        return this.showError(this._('Deleting the customer failed!'))
      }
      this.storeState()
      this.showNotification(this._('Customer deleted!'))
      this.close()
    },
    async save () {
      this.saving = true
      const data = this.getSaveData()
      let customerId
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/customers/' + data.id + '.json?apikey=' + this.apiKey, data)
          customerId = data.id
        } else {
          delete data.id
          response = await axios.post(this.baseDomain + this.shopId + '/customers.json?apikey=' + this.apiKey, data)
          customerId = response.data
        }
      } catch(e) {
        this.saving = false
        return this.showError(e)
      }
      if (customerId) {
        await this.loadCustomer(customerId)
      }
      this.saving = false
      this.saved = true
      this.storeState()
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.customer)
      if (this.saveShouldClose) {
        this.close()
      }
    },
    setParams (params) {
      this.customer = params
    },
    async updateNetvisorCustomers () {
      let response
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/netvisorcustomers.json?apikey=' + this.apiKey, { import: true })
      } catch(e) {
        return false
      }
    },
    searchNetvisorCustomers: debounce(async function (text, done) {
      if (text.length < 2) {
        return
      }
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/netvisorcustomers.json?iSortingCols=1&iSortCol_0=1&sSortDir_0=asc&sSearch=' + escape(text) + '&iDisplayStart=0&iDisplayLength=100&apikey=' + this.apiKey)
      } catch(e) {
        return
      }
      this.netvisorCustomers = response.data
    }),
    formatNetvisorCustomer(nc) {
      if (!nc) {
        return '-'
      }
      return nc.name + ' (' + this._('Netvisor key') + ': ' + nc.netvisorkey + ')'
    },
    async loadBillingCustomers () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/billingcustomers.json?apikey=' + this.apiKey)
      } catch(e) {
        return false
      }
      this.allBillingCustomers = response.data
    },
    async loadDimensions () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/dimensions.json?apikey=' + this.apiKey)
      } catch(e) {
        return false
      }
      let dim = response.data
      let dimensions = []
      for (let i = 0; i < dim.length; i++) {
        if (parseInt(dim[i].level) === 0) {
          let currParent = dim[i]
          let currChildren = []
          for (let j = 0; j < dim.length; j++) {
            if (dim[j].parentid === currParent.id) {
              currChildren.push(dim[j])
            }
          }
          currParent.children = currChildren
          dimensions.push(currParent)
        }
      }
      this.allDimensions = dimensions
    },
    addBillingCustomer () {
      this.showModal('billingcustomerdetails', BillingCustomerDetails, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          component: {
            initData: {
              modal: true
            }
          }
        },
        events: {
          save: this.setAddedBillingCustomer
        },
        handlers: {},
        width: 1000
      })
    },
    setAddedBillingCustomer (bc) {
      console.log(bc)
    },
    addLoyaltyCard () {
      this.showDialog({
        title: this._('Link a Loyalty card'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            requiredFields: ['name', 'code'],
            handler: (data) => {
              this.setAddedLoyaltyCard(data)
            }
          }
        ],
        fields: [
          {
            title: this._('Name'),
            type: 'text',
            name: 'name',
            placeholder: this._('Enter a descriptive name for this card')
          },
          {
            title: this._('Code'),
            type: 'text',
            name: 'code',
            placeholder: this._('Place the cursor in this field and scan the code')
          }
        ]
      })
    },
    setAddedLoyaltyCard(card) {
      if (!this.customer.sharedLoyaltycard) {
        this.customer.sharedLoyaltycard = []
      }
      this.customer.sharedLoyaltycard.push(card)
      this.customer.sharedLoyaltycard = this.customer.sharedLoyaltycard.filter(c => c !== null)
    },
    displayOrder (id, name) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'OrderDetails',
        title: this._('Order') + ' ' + id + ': ' + name,
        width: 6,
        data: {
          orderId: id,
          trail: this.componentTrail
        }
      })
    }
  },
  watch: {
    netvisorCustomer (newValue) {
      if (newValue && newValue.netvisorkey) {
        this.customer.netvisorid = newValue.netvisorkey
      } else {
        this.customer.netvisorid = null
      }
    }
  },
  components: {
    BillingCustomerDetails,
    SubscriptionsPanel,
    Datatable,
    DraggableMultiSelector,
    ModelListSelect,
    PhoneNumberInput
  },
  created () {
    if (this.component && this.component.initData) {
      if (parseInt(this.component.initData.customerId)) {
        this.loadCustomer(this.component.initData.customerId)
      } else if (this.component.initData.customer) {
        this.customer = Object.assign({}, this.component.initData.customer)
      }
    }
  },
  async updated () {
    if (this.component && this.component.initData) {
      if (this.customer && this.customer.id && parseInt(this.customer.id, 10) !== parseInt(this.component.initData.customerId, 10) && !this.deleted) {
        if (parseInt(this.component.initData.customerId)) {
          try {
            await this.loadCustomer(this.component.initData.customerId)
          } catch(e) {}
        }
      }
    }
  },
  async mounted () {
    if (this.selectedCustomer) {
      this.loadCustomer(this.selectedCustomer.id)
    }
    this.loadBillingCustomers()
    this.loadDimensions()
    if(!this.events) {
      let events = {}
      events['showCustomerDetailsParams'] = (message) => {
        this.setParams(message)
      }
      events['customerUpdate'] = async (message) => {
        if (message && message.data && this.customer && parseInt(message.data.id, 10) === parseInt(this.customer.id, 10) && !this.deleted) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            this.loadCustomer(message.data.id)
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
  }
}
</script>